import { useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Info } from 'lucide-react';

export interface HADSResultPayload {
  answers: number[];
  anxiety: number[];
  depression: number[];
  anxietyScore: number;
  depressionScore: number;
  instrumentVersion: 'HADS_BR_BOTEGA_1995';
  scoringVersion: 'HADS_VALIDATED_ITEM_KEY_V1';
  finishedAt?: string;
}

interface HADSTestProps {
  initial?: number[];
  onCancel?: () => void;
  onNext: (result: HADSResultPayload) => void;
}

export const HADS_ITEMS = [
  {
    domain: 'A',
    question: 'Eu me sinto tenso ou contraído:',
    options: ['A maior parte do tempo', 'Boa parte do tempo', 'De vez em quando', 'Nunca'],
  },
  {
    domain: 'D',
    question: 'Eu ainda sinto gosto pelas mesmas coisas de antes:',
    options: ['Sim, do mesmo jeito que antes', 'Não tanto quanto antes', 'Só um pouco', 'Já não sinto mais prazer em nada'],
  },
  {
    domain: 'A',
    question: 'Eu sinto uma espécie de medo, como se alguma coisa ruim fosse acontecer:',
    options: ['Sim, e de um jeito muito forte', 'Sim, mas não tão forte', 'Um pouco, mas isso não me preocupa', 'Não sinto nada disso'],
  },
  {
    domain: 'D',
    question: 'Dou risada e me divirto quando vejo coisas engraçadas:',
    options: ['Do mesmo jeito que antes', 'Atualmente um pouco menos', 'Atualmente bem menos', 'Não consigo mais'],
  },
  {
    domain: 'A',
    question: 'Estou com a cabeça cheia de preocupações:',
    options: ['A maior parte do tempo', 'Boa parte do tempo', 'De vez em quando', 'Raramente'],
  },
  {
    domain: 'D',
    question: 'Eu me sinto alegre:',
    options: ['Nunca', 'Poucas vezes', 'Muitas vezes', 'A maior parte do tempo'],
  },
  {
    domain: 'A',
    question: 'Consigo ficar sentado à vontade e me sentir relaxado:',
    options: ['Sim, quase sempre', 'Muitas vezes', 'Poucas vezes', 'Nunca'],
  },
  {
    domain: 'D',
    question: 'Eu estou lento para pensar e fazer as coisas:',
    options: ['Quase sempre', 'Muitas vezes', 'De vez em quando', 'Nunca'],
  },
  {
    domain: 'A',
    question: 'Eu tenho uma sensação ruim de medo, como um frio na barriga ou um aperto no estômago:',
    options: ['Nunca', 'De vez em quando', 'Muitas vezes', 'Quase sempre'],
  },
  {
    domain: 'D',
    question: 'Eu perdi o interesse em cuidar da minha aparência:',
    options: ['Completamente', 'Não estou mais me cuidando como deveria', 'Talvez não tanto quanto antes', 'Me cuido do mesmo jeito que antes'],
  },
  {
    domain: 'A',
    question: 'Eu me sinto inquieto, como se eu não pudesse ficar parado em lugar nenhum:',
    options: ['Sim, demais', 'Bastante', 'Um pouco', 'Não me sinto assim'],
  },
  {
    domain: 'D',
    question: 'Fico esperando animado as coisas boas que estão por vir:',
    options: ['Do mesmo jeito que antes', 'Um pouco menos do que antes', 'Bem menos do que antes', 'Quase nunca'],
  },
  {
    domain: 'A',
    question: 'De repente, tenho a sensação de entrar em pânico:',
    options: ['A quase todo momento', 'Várias vezes', 'De vez em quando', 'Não sinto isso'],
  },
  {
    domain: 'D',
    question: 'Consigo sentir prazer quando assisto a um bom programa de televisão, de rádio ou quando leio alguma coisa:',
    options: ['Quase sempre', 'Várias vezes', 'Poucas vezes', 'Quase nunca'],
  },
] as const;

export const HADS_ANXIETY_INDICES = [0, 2, 4, 6, 8, 10, 12] as const;
export const HADS_DEPRESSION_INDICES = [1, 3, 5, 7, 9, 11, 13] as const;

const HADS_SCORE_BY_OPTION = [
  [3, 2, 1, 0],
  [0, 1, 2, 3],
  [3, 2, 1, 0],
  [0, 1, 2, 3],
  [3, 2, 1, 0],
  [3, 2, 1, 0],
  [0, 1, 2, 3],
  [3, 2, 1, 0],
  [0, 1, 2, 3],
  [3, 2, 1, 0],
  [3, 2, 1, 0],
  [0, 1, 2, 3],
  [3, 2, 1, 0],
  [0, 1, 2, 3],
] as const;

export const getHADSItemScore = (
  itemIndex: number,
  optionIndex: number,
): number | null => {
  const itemScores = HADS_SCORE_BY_OPTION[itemIndex];
  const score = itemScores?.[optionIndex as 0 | 1 | 2 | 3];
  return typeof score === 'number' ? score : null;
};

export const calculateHADSScores = (answers: readonly number[]) => {
  const scoreDomain = (indices: readonly number[]) => indices.reduce((sum, index) => {
    const score = getHADSItemScore(index, answers[index]);
    return sum + (score ?? 0);
  }, 0);

  return {
    anxiety: HADS_ANXIETY_INDICES.map((index) => getHADSItemScore(index, answers[index]) ?? 0),
    depression: HADS_DEPRESSION_INDICES.map((index) => getHADSItemScore(index, answers[index]) ?? 0),
    anxietyScore: scoreDomain(HADS_ANXIETY_INDICES),
    depressionScore: scoreDomain(HADS_DEPRESSION_INDICES),
  };
};

export default function HADSTest({ initial, onCancel, onNext }: HADSTestProps) {
  const [answers, setAnswers] = useState<number[]>(initial ?? Array(HADS_ITEMS.length).fill(-1));
  const [step, setStep] = useState(0);

  const currentItem = HADS_ITEMS[step];
  const allAnswered = answers.every((answer) => answer >= 0);
  const scores = calculateHADSScores(answers);
  const anxietyScore = scores.anxietyScore;
  const depressionScore = scores.depressionScore;
  const currentDomainLabel = currentItem.domain === 'A' ? 'Domínio A' : 'Domínio D';

  const setAnswer = (index: number, value: number) => {
    setAnswers((previous) => {
      const next = [...previous];
      next[index] = value;
      return next;
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">HADS - Questionário</p>
          <p className="text-sm opacity-90">14 questões. Faixas: 0-7 normal, 8-10 limítrofe, 11-21 anormal.</p>
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <p className="font-medium">Questão {step + 1} de {HADS_ITEMS.length}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">{currentDomainLabel}</p>
          </div>
          <div className="text-sm text-gray-600">Selecionado: {answers[step] >= 0 ? answers[step] : '—'}</div>
        </div>

        <div className="mb-4 rounded-xl border bg-gray-50 p-4">
          <p className="text-sm font-medium text-gray-900">{currentItem.question}</p>
        </div>

        <div className="space-y-2">
          {currentItem.options.map((option, optionIndex) => (
            <label
              key={option}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition ${
                answers[step] === optionIndex ? 'border-blue-600 bg-blue-600 text-white' : 'bg-white hover:bg-slate-50'
              }`}
            >
              <input
                type="radio"
                name={`hads_v2_q_${step}`}
                className="hidden"
                checked={answers[step] === optionIndex}
                onChange={() => setAnswer(step, optionIndex)}
              />
              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-current text-xs font-bold">
                {answers[step] === optionIndex ? <Check className="h-4 w-4" /> : null}
              </div>
              <div className="flex-1 text-sm">{option}</div>
            </label>
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2 hover:bg-gray-200 disabled:opacity-50"
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            disabled={step === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </button>

          {step < HADS_ITEMS.length - 1 ? (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={() => setStep((current) => Math.min(HADS_ITEMS.length - 1, current + 1))}
              disabled={answers[step] < 0}
            >
              Próxima
              <ChevronRight className="ml-1 h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white hover:bg-green-700 disabled:opacity-50"
              disabled={!allAnswered}
              onClick={() =>
                allAnswered &&
                onNext({
                  answers,
                  anxiety: scores.anxiety,
                  depression: scores.depression,
                  anxietyScore,
                  depressionScore,
                  instrumentVersion: 'HADS_BR_BOTEGA_1995',
                  scoringVersion: 'HADS_VALIDATED_ITEM_KEY_V1',
                  finishedAt: new Date().toISOString(),
                })
              }
            >
              Concluir HADS
              <Check className="ml-1 h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <div className="text-right text-sm text-gray-700">
          <div>Domínio A: {anxietyScore}</div>
          <div>Domínio D: {depressionScore}</div>
        </div>
      </div>
    </div>
  );
}
